import React from 'react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';

/** Same pattern as ReportGenerationModal email validation. */
export const SEQUENCE_NOTIFY_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidNotifyEmail(email: string): boolean {
  return SEQUENCE_NOTIFY_EMAIL_REGEX.test(email.trim());
}

export interface CompletionEmailNotifyBlockProps {
  disabled?: boolean;
  notifyEnabled: boolean;
  onNotifyEnabledChange: (checked: boolean) => void;
  emails: string[];
  newEmail: string;
  onNewEmailChange: (value: string) => void;
  emailError: string;
  onAddEmail: () => void;
  onRemoveEmail: (email: string) => void;
  onNewEmailKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Completion notification emails for async jobs (e.g. pipe-based sequence generate).
 * UI/validation aligned with ReportGenerationModal email list pattern.
 */
export const CompletionEmailNotifyBlock: React.FC<CompletionEmailNotifyBlockProps> = ({
  disabled = false,
  notifyEnabled,
  onNotifyEnabledChange,
  emails,
  newEmail,
  onNewEmailChange,
  emailError,
  onAddEmail,
  onRemoveEmail,
  onNewEmailKeyDown,
}) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 space-y-3">
      <div className="flex items-start gap-3">
        <Checkbox
          id="sequence-notify-email"
          checked={notifyEnabled}
          onCheckedChange={(v) => onNotifyEnabledChange(v === true)}
          disabled={disabled}
          className="mt-0.5"
        />
        <div className="space-y-0.5 flex-1">
          <Label htmlFor="sequence-notify-email" className="text-sm font-medium text-gray-900 cursor-pointer">
            Notify me when generation completes
          </Label>
          <p className="text-xs text-gray-500">
            We will email you when the sequence job finishes. You can add additional recipients.
          </p>
        </div>
      </div>

      {notifyEnabled && (
        <div className="space-y-3 pt-1 border-t border-gray-200">
          <Label className="text-sm font-medium text-gray-700">Email addresses</Label>

          {emails.length > 0 && (
            <ul className="space-y-2" aria-label="Notification email list">
              {emails.map((email, index) => (
                <li
                  key={`${email}-${index}`}
                  className="flex flex-col gap-2 p-3 bg-white rounded-lg border border-gray-100 sm:flex-row sm:items-center sm:gap-3"
                >
                  <span className="text-sm text-gray-600 shrink-0">{index + 1}.</span>
                  <span className="text-sm text-gray-900 flex-1 min-w-0 break-all">{email}</span>
                  <Button
                    type="button"
                    onClick={() => onRemoveEmail(email)}
                    size="sm"
                    variant="outline"
                    className="min-h-9 px-3 shrink-0 border-gray-300 text-gray-800 hover:bg-gray-50 self-start sm:self-center"
                    disabled={disabled}
                    aria-label={`Remove email ${email}`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter email address"
                value={newEmail}
                onChange={(e) => onNewEmailChange(e.target.value)}
                onKeyDown={onNewEmailKeyDown}
                className={cn(emailError ? 'border-red-500' : '')}
                disabled={disabled}
              />
              {emailError ? <p className="text-xs text-red-600 mt-1">{emailError}</p> : null}
            </div>
            <Button
              type="button"
              onClick={onAddEmail}
              size="sm"
             // variant="outline"
             // className="min-h-9 px-3 shrink-0 border-gray-300 text-gray-800 hover:bg-gray-50 inline-flex items-center gap-1.5"
              disabled={disabled}
              aria-label="Add email to notification list"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span>Add email</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
